<template>
    <div class="py-12">
        <div class="mb-4 flex justify-end">
            <AtomicButton color="yellow" size="md" @click="openCreate">
                {{ t("vue.create_account") ?? "Create account" }}
            </AtomicButton>
        </div>

        <DataTable
            :title="t('vue.teams_accounts')"
            searchable
            :headers="headers"
            :items="teams"
        >
            <template #item-profile_photo_url="{ data }">
                <div class="flex justify-center">
                    <img
                        v-if="data"
                        class="h-10 w-10 rounded-full"
                        :src="data as string"
                        alt="Team profile photo record"
                    />
                    <img
                        v-else
                        class="h-10 w-10 rounded-full"
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/600px-No_image_available.svg.png"
                        alt="Team profile photo stock image fallback"
                    />
                </div>
            </template>

            <template #item-custom="{ row }">
                <div class="flex w-full justify-center">
                    <AtomicButton
                        color="white"
                        size="sm"
                        @click="handleShowTeam(row)"
                    >
                        <template #icon>
                            <MagnifyingGlassIcon class="h-6 w-6" />
                        </template>
                    </AtomicButton>
                </div>
            </template>
        </DataTable>

        <Modal v-if="showCreateModal" size="md" @close="closeCreate">
            <template #header>
                <div class="text-gray-600">Create account</div>
            </template>

            <template #body>
                <div class="space-y-4 p-6">
                    <div>
                        <label
                            class="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Name
                        </label>
                        <input
                            v-model="form.name"
                            type="text"
                            class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            placeholder="Demo account"
                        />
                    </div>

                    <div>
                        <label
                            class="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Owner
                        </label>
                        <div
                            v-if="selectedOwner"
                            class="mb-2 flex items-center justify-between rounded-md bg-gray-100 px-3 py-2 text-sm"
                        >
                            <span>
                                {{ selectedOwner.name }} ({{
                                    selectedOwner.email
                                }})
                            </span>
                            <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-800"
                                @click="clearOwner"
                            >
                                Change
                            </button>
                        </div>
                        <template v-else>
                            <input
                                v-model="ownerSearch"
                                type="text"
                                class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                                placeholder="Search users by name or e-mail"
                                @input="searchOwners"
                            />
                            <ul
                                v-if="ownerResults.length"
                                class="mt-1 max-h-40 overflow-auto rounded-md border border-gray-200 text-sm"
                            >
                                <li
                                    v-for="user in ownerResults"
                                    :key="user.id"
                                    class="cursor-pointer px-3 py-2 hover:bg-gray-100"
                                    @click="selectOwner(user)"
                                >
                                    {{ user.name }} ({{ user.email }})
                                </li>
                            </ul>
                        </template>
                    </div>

                    <label
                        class="flex items-center gap-2 text-sm text-gray-700"
                    >
                        <input
                            v-model="form.god_mode"
                            type="checkbox"
                            class="rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                        />
                        God Mode (full access — recommended for demo accounts)
                    </label>

                    <p v-if="createError" class="text-sm text-red-600">
                        {{ createError }}
                    </p>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between border-t border-gray-200 p-6">
                    <AtomicButton color="white" size="md" @click="closeCreate">
                        Cancel
                    </AtomicButton>
                    <AtomicButton
                        color="yellow"
                        size="md"
                        :disabled="!canSubmit"
                        @click="submitCreate"
                    >
                        Create
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts" setup>
import type IPaginationData from "@/types/Atomic/IPaginationData";
import { MagnifyingGlassIcon } from "@heroicons/vue/24/outline";
import route from "ziggy-js";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import { router, usePage } from "@inertiajs/vue3";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { computed, reactive, ref } from "vue";
import { useTranslations } from "@/composables/useTranslations";

interface IOwnerOption {
    id: number;
    name: string;
    email: string;
}

defineProps<{
    teams: IPaginationData<ICurrentTeam>;
}>();

// Get access to translations
const { t } = useTranslations();

const globalProps = computed(() => usePage<IRhizomPageProps>().props);

const showCreateModal = ref(false);
const ownerSearch = ref("");
const ownerResults = ref<IOwnerOption[]>([]);
const selectedOwner = ref<IOwnerOption | null>(null);
const createError = ref<string | null>(null);
const form = reactive({
    name: "",
    god_mode: true,
});

const canSubmit = computed(
    () => form.name.trim().length > 0 && selectedOwner.value !== null,
);

const openCreate = () => {
    createError.value = null;
    form.name = "";
    form.god_mode = true;
    ownerSearch.value = "";
    ownerResults.value = [];

    // Preselect the currently logged-in admin as the owner.
    const currentUser = globalProps.value.user;
    selectedOwner.value = currentUser
        ? {
              id: currentUser.id,
              name: currentUser.name,
              email: currentUser.email,
          }
        : null;

    showCreateModal.value = true;
};

const closeCreate = () => {
    showCreateModal.value = false;
};

const clearOwner = () => {
    selectedOwner.value = null;
    ownerSearch.value = "";
    ownerResults.value = [];
};

const selectOwner = (user: IOwnerOption) => {
    selectedOwner.value = user;
    ownerResults.value = [];
};

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const searchOwners = () => {
    if (searchTimeout) {
        clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(async () => {
        try {
            const response = await axios.get(
                route("kiosk.teams.owner-search", { q: ownerSearch.value }),
            );
            ownerResults.value = response.data;
        } catch (error) {
            ownerResults.value = [];
        }
    }, 250);
};

const submitCreate = () => {
    if (!canSubmit.value || !selectedOwner.value) {
        return;
    }

    createError.value = null;

    router.post(
        route("kiosk.teams.store"),
        {
            name: form.name.trim(),
            owner_id: selectedOwner.value.id,
            god_mode: form.god_mode,
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                showCreateModal.value = false;
            },
            onError: (errors) => {
                createError.value =
                    Object.values(errors)[0] ?? "Could not create the account.";
            },
        },
    );
};

const headers: IDataTableHeader[] = [
    { text: "", id: "profile_photo_url" },
    {
        text: t("vue.id"),
        id: "id",
        sortable: true,
        sortableId: "id",
    },
    {
        text: t("vue.name"),
        id: "name",
        sortable: true,
        sortableId: "name",
    },
    {
        text: t("vue.owner_email"),
        id: "email",
        sortable: true,
        sortableId: "email",
    },
    {
        text: t("vue.domains_count"),
        id: "domains_quantity",
        sortable: true,
        sortableId: "domains_quantity",
    },
    {
        text: t("vue.subscription_level"),
        id: "subscription_level",
        sortable: true,
        sortableId: "subscription_level",
    },
    {
        text: t("vue.subscription_period"),
        id: "subscription_period",
    },
    {
        text: t("vue.subscription_amount"),
        sortable: true,
        id: "paddle_sub_amount",
        sortableId: "paddle_sub_amount",
    },
    {
        text: t("vue.paddle_sub_last_transaction_amount"),
        sortable: true,
        id: "paddle_sub_last_transaction_amount",
        sortableId: "paddle_sub_last_transaction_amount",
    },
    {
        text: t("vue.fae"),
        sortable: true,
        id: "had_active_domain",
        sortableId: "had_active_domain",
    },
    { text: "", id: "custom" },
];

const handleShowTeam = function (team: ICurrentTeam) {
    router.get(
        route("kiosk.teams.members.index", {
            team: team.id,
        }),
    );
};
</script>

<style></style>
